import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer } from 'recharts';
import { Card, CardHeader, CardTitle, CardContent } from '../../../elements/ui/card';
import EmptyState from './EmptyState';
import { useTranslation } from 'react-i18next';
import { formatTimeLabel, hasValues, mergeSeries, timeAxisProps, type TimeRange } from './timeSeries';
import type { LineSeries } from '../../../models/time-series';

interface HumidityLineChartProps {
  title: string;
  series: LineSeries[];
  range: TimeRange;
  lowThreshold?: number;
  highThreshold?: number;
}

function CustomTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) {
    return null;
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-md text-xs">
      <p className="font-medium text-gray-700 mb-1">{formatTimeLabel(label)}</p>
      {payload.map((entry: any) => (
        <div key={entry.name} className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.stroke }} />
          <span className="text-gray-600">{entry.name}:</span>
          <span className="font-medium text-gray-900">{entry.value != null ? `${entry.value} %H` : '—'}</span>
        </div>
      ))}
    </div>
  );
}

const HumidityLineChart: React.FC<HumidityLineChartProps> = ({ title, series, range, lowThreshold, highThreshold }) => {
  const { t } = useTranslation();

  const mergedData = React.useMemo(() => mergeSeries(series, range), [series, range]);
  const hasData = hasValues(series);

  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle>{title}</CardTitle>
          <div className="flex items-center gap-3">
            {series.map((s) => (
              <div key={s.name} className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: s.strokeColor }} />
                <span className="text-xs text-gray-500">{s.name}</span>
              </div>
            ))}
            {lowThreshold != null && (
              <div className="flex items-center gap-1.5">
                <div className="w-4 h-0 border-t-2 border-dashed border-amber-500" />
                <span className="text-xs text-gray-500">
                  {t('f-humidity-low', { defaultValue: 'Low' })} {lowThreshold}%
                </span>
              </div>
            )}
            {highThreshold != null && (
              <div className="flex items-center gap-1.5">
                <div className="w-4 h-0 border-t-2 border-dashed border-red-500" />
                <span className="text-xs text-gray-500">
                  {t('f-humidity-high', { defaultValue: 'High' })} {highThreshold}%
                </span>
              </div>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {!hasData ? (
          <EmptyState
            title={`${t('f-no-data-placeholder', { value: title.toUpperCase() })}`}
            description={`${t('f-sensor-not-available')}`}
          />
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={mergedData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis {...timeAxisProps(range)} />
              <YAxis
                tick={{ fontSize: 11, fill: '#9ca3af' }}
                axisLine={false}
                tickLine={false}
                width={40}
                domain={[0, 100]}
                tickFormatter={(v) => `${v}%`}
              />
              <Tooltip content={<CustomTooltip />} />
              {series.map((s) => (
                <Line
                  key={s.name}
                  type="monotone"
                  dataKey={s.name}
                  stroke={s.strokeColor}
                  strokeWidth={2}
                  dot={false}
                  connectNulls={false}
                />
              ))}
              {highThreshold != null && (
                <ReferenceLine
                  y={highThreshold}
                  stroke="#ef4444"
                  strokeDasharray="5 5"
                  label={{ value: `High ${highThreshold}%H`, position: 'right', fill: '#ef4444', fontSize: 11 }}
                />
              )}
              {lowThreshold != null && (
                <ReferenceLine
                  y={lowThreshold}
                  stroke="#f59e0b"
                  strokeDasharray="5 5"
                  label={{ value: `Low ${lowThreshold}%H`, position: 'right', fill: '#f59e0b', fontSize: 11 }}
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default HumidityLineChart;
